import React from 'react';
import {lazy, Suspense} from 'react';
import {Outlet, Navigate} from 'react-router-dom';
// Guards
import {AuthGuard, RoleBasedGuard, ModuleAccessGuard, FeatureAccessGuard, FeatureAccessGuardAny} from 'packages/common/guards';
// Settings config
import {SETTINGS_FEATURES} from 'apps/dashboard/pages/app/settings/config/settingsSections';
// Layouts
import DynamicDashboardLayout from 'packages/common/layouts/app/dynamic-layout';
// Components
import {LoadingScreen} from 'packages/common/components/loading-screen';
// Roles & Paths
import {paths} from 'packages/common/routes/paths';
import {routeRoles} from 'packages/common/utils/rolesConfig';
// Context
import {AuthProvider} from 'packages/common/contexts/auth-context';
import {AdminAccessProvider} from 'packages/common/contexts/admin-access-context';
import SessionProvider from 'packages/common/providers/SessionProvider';
// PWA
import InstallPrompt from 'apps/dashboard/src/pwa/components/InstallPrompt';

// Lazy-loaded Pages
// Global Pages
const DashboardPage = lazy(() => import('apps/dashboard/pages/app/dashboard/view/DashboardPage'));
const SettingsPage = lazy(() => import('apps/dashboard/pages/app/settings/view/SettingsPage'));
const UserAccountPage = lazy(() => import('apps/dashboard/pages/app/account/view/UserAccountPage'));
const StatisticsPage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/statistics/view/StatisticsPage'));
// Giftvouchers
const GiftVoucherCatalog = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogPage'));
const GiftVouchersTypeCreate = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogCreatePage'));
const GiftVouchersTypeEdit = lazy(() => import('apps/dashboard/pages/app/gift-voucher/catalog/views/CatalogEditPage'));
const GiftVouchersManage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/manage/view/ManagePage'));
const GiftVouchersDetails = lazy(() => import('apps/dashboard/pages/app/gift-voucher/details/view/DetailsPage'));
// Promotions
const PromotionsListPage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/promotions/views/PromotionsListPage'));
const PromotionCreatePage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/promotions/views/PromotionCreatePage'));
const PromotionEditPage = lazy(() => import('apps/dashboard/pages/app/gift-voucher/promotions/views/PromotionEditPage'));
// Customers
const CustomersListPage = lazy(() => import('apps/dashboard/pages/app/customers/view/CustomersListPage'));
const CustomerDetailsPage = lazy(() => import('apps/dashboard/pages/app/customers/view/CustomerDetailsPage'));
const CustomerDuplicatesPage = lazy(() => import('apps/dashboard/pages/app/customers/view/CustomerDuplicatesPage'));
// Wellness
const ServiceCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/services/view/ServiceCreatePage'));
const ServiceEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/services/view/ServiceEditPage'));
const ExtraCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/extras/view/ExtraCreatePage'));
const ExtraEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/extras/view/ExtraEditPage'));
const ProductCreatePage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/products/view/ProductCreatePage'));
const ProductEditPage = lazy(() => import('apps/dashboard/pages/app/wellness/catalog/products/view/ProductEditPage'));
const CalendarPage = lazy(() => import('apps/dashboard/pages/app/wellness/calendar/view/CalendarPage'));
const WellnessBookingsPage = lazy(() => import('apps/dashboard/pages/app/wellness/booking/view/BookingsPage'));
const WellnessPOSPage = lazy(() => import('apps/dashboard/pages/app/wellness/pos/view/POSPage'));
const WellnessPOSPaymentPage = lazy(() => import('apps/dashboard/pages/app/wellness/pos/view/POSPaymentPage'));
// Restaurant
const RestaurantSeatingEditorPage = lazy(() => import('apps/dashboard/pages/app/restaurants/seating/view/SeatingEditorPage'));
const RestaurantSeatingPrioritiesPage = lazy(() => import('apps/dashboard/pages/app/restaurants/seating/view/SeatingPrioritiesPage'));
const RestaurantSeatingDisplayOrderPage = lazy(() => import('apps/dashboard/pages/app/restaurants/seating/view/SeatingDisplayOrderPage'));
const RestaurantReservationsPage = lazy(() => import('apps/dashboard/pages/app/restaurants/reservations/view/ReservationsPage'));
const RestaurantWaitlistPage = lazy(() => import('apps/dashboard/pages/app/restaurants/waitlist/view/WaitlistPage'));
const RestaurantMessagesPage = lazy(() => import('apps/dashboard/pages/app/restaurants/messages/view/MessagesPage'));
const RestaurantReviewsPage = lazy(() => import('apps/dashboard/pages/app/restaurants/reviews/view/ReviewsPage'));
const RestaurantStatisticsPage = lazy(() => import('apps/dashboard/pages/app/restaurants/statistics/view/StatisticsPage'));
// Newsletter
const NewsletterCampaignsPage = lazy(() => import('apps/dashboard/pages/app/newsletter/list/view/CampaignsListPage'));
const NewsletterCampaignEditPage = lazy(() => import('apps/dashboard/pages/app/newsletter/edit/view/CampaignEditPage'));
const NewsletterCampaignConfigurePage = lazy(() =>
  import('apps/dashboard/pages/app/newsletter/configure/view/CampaignConfigurePage'),
);
const SmsWalletPurchasePage = lazy(() => import('apps/dashboard/pages/app/sms-wallet/view/SmsWalletPurchasePage'));

export const appRoutes = [
  {
    path: '/dashboard',
    element: (
      <AuthProvider>
        <AdminAccessProvider>
          <AuthGuard>
            <SessionProvider>
              <RoleBasedGuard roles={routeRoles[paths.app.root]}>
                <DynamicDashboardLayout>
                  <Suspense fallback={<LoadingScreen />}>
                    <Outlet />
                  </Suspense>
                  <InstallPrompt />
                </DynamicDashboardLayout>
              </RoleBasedGuard>
            </SessionProvider>
          </AuthGuard>
        </AdminAccessProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: '',
        element: <DashboardPage />,
        index: true,
      },
      {
        path: 'user-account',
        element: <UserAccountPage />,
      },

      // Route Settings - accessible si l'utilisateur a au moins une feature settings
      {path: 'settings', element: <FeatureAccessGuardAny features={SETTINGS_FEATURES}><SettingsPage /></FeatureAccessGuardAny>},
      // Editeur de plan de salle — route soeur de settings, layout fullscreen independant
      {path: 'settings/seating/:areaId', element: <FeatureAccessGuard feature="restaurant.seating"><RestaurantSeatingEditorPage /></FeatureAccessGuard>},
      // Tri global de l'ordre d'attribution des tables — route soeur de l'editeur de plan
      {path: 'settings/seating-priorities', element: <FeatureAccessGuard feature="restaurant.seating"><RestaurantSeatingPrioritiesPage /></FeatureAccessGuard>},
      // Ordre d'affichage des tables a la saisie backoffice — route soeur, par salle
      {path: 'settings/table-display-order', element: <FeatureAccessGuard feature="restaurant.seating"><RestaurantSeatingDisplayOrderPage /></FeatureAccessGuard>},
      // Creation/edition du catalogue wellness — routes soeurs de settings, plein ecran
      {
        path: 'settings/wellness/catalog',
        element: <ModuleAccessGuard moduleKey="wellness"><FeatureAccessGuard feature="wellness.catalog"><Outlet /></FeatureAccessGuard></ModuleAccessGuard>,
        children: [
          {path: 'services/create', element: <ServiceCreatePage />},
          {path: 'services/edit/:id', element: <ServiceEditPage />},
          {path: 'extras/create', element: <ExtraCreatePage />},
          {path: 'extras/edit/:id', element: <ExtraEditPage />},
          {path: 'products/create', element: <ProductCreatePage />},
          {path: 'products/edit/:id', element: <ProductEditPage />},
        ],
      },

      // Section gift-voucher avec ModuleAccessGuard + FeatureAccessGuard par feature
      {
        path: 'gift-voucher',
        element: <ModuleAccessGuard moduleKey="giftvouchers"><Outlet /></ModuleAccessGuard>,
        children: [
          // Catalog - giftVoucher.catalog
          {index: true, element: <FeatureAccessGuard feature="giftVoucher.catalog"><GiftVoucherCatalog /></FeatureAccessGuard>},
          {path: 'catalog', element: <FeatureAccessGuard feature="giftVoucher.catalog"><GiftVoucherCatalog /></FeatureAccessGuard>},
          {path: 'catalog/create/:type', element: <FeatureAccessGuard feature="giftVoucher.catalog"><GiftVouchersTypeCreate /></FeatureAccessGuard>},
          {path: 'catalog/edit/:type/:id', element: <FeatureAccessGuard feature="giftVoucher.catalog"><GiftVouchersTypeEdit /></FeatureAccessGuard>},
          // Manage - giftVoucher.manage
          {path: 'manage', element: <FeatureAccessGuard feature="giftVoucher.manage"><GiftVouchersManage /></FeatureAccessGuard>},
          {path: 'details/:id', element: <FeatureAccessGuard feature="giftVoucher.manage"><GiftVouchersDetails /></FeatureAccessGuard>},
          // Promotions - giftVoucher.promotions
          {path: 'promotions', element: <FeatureAccessGuard feature="giftVoucher.promotions"><PromotionsListPage /></FeatureAccessGuard>},
          {path: 'promotions/create', element: <FeatureAccessGuard feature="giftVoucher.promotions"><PromotionCreatePage /></FeatureAccessGuard>},
          {path: 'promotions/edit/:id', element: <FeatureAccessGuard feature="giftVoucher.promotions"><PromotionEditPage /></FeatureAccessGuard>},
          // Statistics - giftVoucher.statistics
          {path: 'statistics', element: <FeatureAccessGuard feature="giftVoucher.statistics"><StatisticsPage /></FeatureAccessGuard>},
        ],
      },

      // Section wellness avec ModuleAccessGuard + FeatureAccessGuard par feature
      {
        path: 'wellness',
        element: <ModuleAccessGuard moduleKey="wellness"><Outlet /></ModuleAccessGuard>,
        children: [
          {index: true, element: <Navigate to="calendar" replace />},
          // Catalog : déplacé dans les paramètres (section Wellness), y compris
          // les pages create/edit (routes sœurs de settings ci-dessus)
          {path: 'catalog/*', element: <Navigate to={paths.app.settings.wellness.catalog} replace />},
          // Spaces : déplacé dans les paramètres (section Wellness)
          {path: 'spaces', element: <Navigate to={paths.app.settings.wellness.spaces} replace />},
          // Calendar - wellness.calendar
          {path: 'calendar', element: <FeatureAccessGuard feature="wellness.calendar"><CalendarPage /></FeatureAccessGuard>},
          // Bookings - wellness.bookings
          {path: 'bookings', element: <FeatureAccessGuard feature="wellness.bookings"><WellnessBookingsPage /></FeatureAccessGuard>},
          // POS - wellness.pos
          {
            path: 'pos',
            element: <FeatureAccessGuard feature="wellness.pos"><Outlet /></FeatureAccessGuard>,
            children: [
              {index: true, element: <Navigate to="sale" replace />},
              {path: 'sale', element: <WellnessPOSPage />},
              {path: 'closing', element: <WellnessPOSPage />},
              {path: 'reports', element: <WellnessPOSPage />},
              {path: 'payment', element: <WellnessPOSPaymentPage />},
            ],
          },
        ],
      },

      // Section restaurants avec ModuleAccessGuard + FeatureAccessGuard par feature
      {
        path: 'restaurant',
        element: <ModuleAccessGuard moduleKey="restaurant"><Outlet /></ModuleAccessGuard>,
        children: [
          {index: true, element: <Navigate to="reservations" replace />},
          // Reservations - restaurant.reservations
          {path: 'reservations', element: <FeatureAccessGuard feature="restaurant.reservations"><RestaurantReservationsPage /></FeatureAccessGuard>},
          // Liste d'attente - même feature que reservations (sous-vue dédiée)
          {path: 'waitlist', element: <FeatureAccessGuard feature="restaurant.reservations"><RestaurantWaitlistPage /></FeatureAccessGuard>},
          // Messagerie client - même feature que reservations (une discussion par réservation)
          {path: 'messages', element: <FeatureAccessGuard feature="restaurant.reservations"><RestaurantMessagesPage /></FeatureAccessGuard>},
          // Reviews - restaurant.reviews
          {path: 'reviews', element: <FeatureAccessGuard feature="restaurant.reviews"><RestaurantReviewsPage /></FeatureAccessGuard>},
          // Statistics - restaurant.statistics
          {path: 'statistics', element: <FeatureAccessGuard feature="restaurant.statistics"><RestaurantStatisticsPage /></FeatureAccessGuard>},
        ],
      },

      // Page de recharge du portefeuille SMS — accessible depuis n'importe quel module
      {path: 'sms-wallet', element: <SmsWalletPurchasePage />},

      // Newsletters - transverse aux modules metier, mais en deploiement
      // progressif : le module est ouvert etablissement par etablissement depuis
      // le panel super admin. Redirection plutot qu'ecran d'erreur, pour ne pas
      // reveler la fonctionnalite a qui ne l'a pas encore. Sans ce garde, un
      // CompanyAdmin y accedait en tapant l'URL : checkFeatureAccess laisse
      // passer ce role sans regarder le module.
      {
        path: 'newsletter',
        element: (
          <ModuleAccessGuard moduleKey="newsletter" redirectTo={paths.app.root}>
            <FeatureAccessGuard feature="newsletter.campaigns"><Outlet /></FeatureAccessGuard>
          </ModuleAccessGuard>
        ),
        children: [
          {index: true, element: <NewsletterCampaignsPage />},
          {path: ':campaignId', element: <NewsletterCampaignEditPage />},
          {path: ':campaignId/configuration', element: <NewsletterCampaignConfigurePage />},
        ],
      },

      // Routes Customers - accessible avec la feature 'customers'
      {
        path: 'customers',
        element: <FeatureAccessGuard feature="customers"><Outlet /></FeatureAccessGuard>,
        children: [
          {index: true, element: <CustomersListPage />},
          {path: 'duplicates', element: <CustomerDuplicatesPage />},
          {path: 'details/:customerId', element: <CustomerDetailsPage />},
        ],
      },

      // Employees : déplacé dans les paramètres (section Wellness)
      {
        path: 'employees',
        element: <Navigate to={paths.app.settings.wellness.employees} replace />,
      },
    ],
  },
];
